import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaIcon } from '../../FaIcon';
import { Switch } from '../../buttons/Swtich';
import { AccessService } from '../../../utils/AccessService';
import { LocalStorage } from '../../../utils/LocalStorage';
import { usePortalSubscriptionGating } from '../../../utils/usePortalSubscriptionGating';
import { ClientDashboardColumnsSetsApi } from '../../../api/clientDashboardColumnsSets';
import { PortalReportsApi } from '../../../api/portalReports';
import { InformesApi, isOk, listOf } from '../../../api/informesApi';
import { isClientReportsModality } from '../../../hooks/useReportModality';
import { ClientPortalDashboardSection } from '../ClientPortalDashboardSection';
import { ClientPortalReportsSection } from '../ClientPortalReportsSection';
import { ClientPortalInformesSection } from '../ClientPortalInformesSection';
import { PortalModuleCard } from './PortalModuleCard';
import { anchorAttr } from '../../../guides/anchors';

/**
 * Grilla de módulos del portal del cliente (Tablero / Vistas / Conexiones / MCP).
 * Cada card tiene un master toggle (capacidad por defecto del portal); los módulos con
 * config agencia-side (Tablero / Vistas) abren un panel full-width debajo de la grilla,
 * con una flecha que conserva la continuidad card ➜ panel.
 *
 * Memoria del cliente va aparte, en una franja discreta debajo (después del panel): es
 * SuperAdmin-only mientras el feature está en dogfood, así que no entra a la grilla del
 * cliente (evita la 5ta card huérfana) y se muestra con un tratamiento sobrio de "interno".
 *
 * El módulo de reportes cambia de contenido con la modalidad de la agencia: vistas del
 * Explorador (`DATA_EXPLORER`) o Informes sobre bloques (`REPORT_WITH_TEMPLATES`). La
 * modalidad se lee con el helper compartido, no con una constante local: la copia de ese
 * string acá interpretaba `REPORT_WITH_TEMPLATES` con su sentido VIEJO (el reporte por
 * plantilla del portal V1) y dejaba a la agencia sin poder asociar sus informes.
 */
export function PortalModulesGrid({
    clientId, modules, onToggleModule, isSuperAdmin,
    reportModality, onAssignmentsChanged,
}) {
    const { t } = useTranslation();
    const { canAssignToPortal, loading: gatingLoading } = usePortalSubscriptionGating();
    const [expandedKey, setExpandedKey] = useState(null);
    const [dashboardCount, setDashboardCount] = useState(null);
    const [reportsCount, setReportsCount] = useState(null);
    const [agencyHasConnect, setAgencyHasConnect] = useState(null);

    const isInformesModality = isClientReportsModality(reportModality);

    // Conteos iniciales para el resumen de las cards (sin tener que expandir). Mientras un
    // módulo está expandido, su sección reporta el conteo en vivo vía onCountChange.
    useEffect(() => {
        if (!clientId) return;
        ClientDashboardColumnsSetsApi.listAssignments(clientId).then((res) => {
            const data = (res && (res.data || res)) || {};
            if (Array.isArray(data.sets)) setDashboardCount(data.sets.length);
        }).catch(() => {});
        if (isInformesModality) {
            InformesApi.list(clientId).then((res) => {
                if (!isOk(res)) return;
                setReportsCount(listOf(res, 'client_reports').filter((i) => i.is_published).length);
            }).catch(() => {});
        } else {
            PortalReportsApi.listViews(clientId).then((res) => {
                if (res && Array.isArray(res.views)) {
                    setReportsCount(res.views.filter((v) => v.assigned_to_portal).length);
                }
            }).catch(() => {});
        }
    }, [clientId, isInformesModality]);

    // ¿La agencia tiene el módulo Connect (DASHBO_MCP) en su plan? El feature "connect" del
    // cliente solo tiene efecto si la agencia lo tiene (mismo patrón que ClientFeatureToggles).
    useEffect(() => {
        const userId = LocalStorage.fetchUserId();
        if (!userId) { setAgencyHasConnect(false); return; }
        AccessService.GetUserModules(userId, (data) => {
            const mods = data.modules || [];
            setAgencyHasConnect(mods.includes(AccessService.MODULE_DASHBO_MCP));
        }, () => setAgencyHasConnect(false));
    }, []);

    const toggleExpand = (key) => setExpandedKey((prev) => (prev === key ? null : key));

    // El módulo es el MISMO (la capacidad `reports` del portal); lo que cambia con la
    // modalidad es qué ve el cliente adentro, así que también su nombre y su descripción.
    const reportsModuleName = t(isInformesModality
        ? 'portal_modules.module_informes_name' : 'portal_modules.module_reports_name');

    const reportsSummary = reportsCount == null ? null : t(
        isInformesModality ? 'portal_modules.module_informes_summary' : 'portal_modules.module_reports_summary',
        { count: reportsCount });

    // Warning: módulo activado pero sin contenido asignado → el cliente vería un módulo vacío.
    const dashboardWarning = (modules.dashboard && dashboardCount === 0)
        ? t('portal_modules.module_dashboard_empty_warning') : null;
    const reportsWarning = (modules.reports && reportsCount === 0)
        ? t(isInformesModality
            ? 'portal_modules.module_informes_empty_warning'
            : 'portal_modules.module_reports_empty_warning') : null;

    const renderPanel = () => {
        if (expandedKey === 'dashboard') {
            return <ClientPortalDashboardSection clientId={clientId} onCountChange={setDashboardCount} />;
        }
        if (expandedKey === 'reports') {
            return isInformesModality ? (
                <ClientPortalInformesSection clientId={clientId} onCountChange={setReportsCount}
                                             onPublished={onAssignmentsChanged} />
            ) : (
                <ClientPortalReportsSection clientId={clientId} onCountChange={setReportsCount}
                                            onAssigned={onAssignmentsChanged} />
            );
        }
        return null;
    };

    const panel = renderPanel();

    return (
        <div className="pm-modules">
            <div className="pm-modules-header">
                <h3 className="pm-modules-title">{t('portal_modules.section_title')}</h3>
                <p className="pm-modules-subtitle">{t('portal_modules.section_subtitle')}</p>
            </div>

            <div className="pm-modules-grid" {...anchorAttr('portal_modules')}>
                {/* Tablero / Líneas de control */}
                <PortalModuleCard
                    icon="chart-pie"
                    name={t('portal_modules.module_dashboard_name')}
                    description={t('portal_modules.module_dashboard_desc')}
                    summary={dashboardCount != null ? t('portal_modules.module_dashboard_summary', { count: dashboardCount }) : null}
                    warning={dashboardWarning}
                    enabled={!!modules.dashboard}
                    onToggleEnabled={(v) => onToggleModule('dashboard', v)}
                    expandable
                    active={expandedKey === 'dashboard'}
                    onToggleExpand={() => toggleExpand('dashboard')}
                />

                {/* Vistas del explorador (DATA_EXPLORER) o Informes (REPORT_WITH_TEMPLATES) */}
                <PortalModuleCard
                    icon={isInformesModality ? 'file-lines' : 'binoculars'}
                    name={reportsModuleName}
                    description={t(isInformesModality
                        ? 'portal_modules.module_informes_desc' : 'portal_modules.module_reports_desc')}
                    summary={reportsSummary}
                    warning={reportsWarning}
                    enabled={!!modules.reports}
                    onToggleEnabled={(v) => onToggleModule('reports', v)}
                    toggleDisabled={!gatingLoading && !canAssignToPortal}
                    toggleHint={t('portal_modules.module_reports_upgrade_hint')}
                    expandable
                    active={expandedKey === 'reports'}
                    onToggleExpand={() => toggleExpand('reports')}
                />

                {/* Conexiones */}
                <PortalModuleCard
                    icon="plug"
                    name={t('portal_modules.module_connections_name')}
                    description={t('portal_modules.module_connections_desc')}
                    enabled={!!modules.connections}
                    onToggleEnabled={(v) => onToggleModule('connections', v)}
                    expandable={false}
                />

                {/* MCP / Asistente */}
                <PortalModuleCard
                    icon="microchip"
                    name={t('portal_modules.module_mcp_name')}
                    description={t('portal_modules.module_mcp_desc')}
                    enabled={!!modules.connect}
                    onToggleEnabled={(v) => onToggleModule('connect', v)}
                    expandable={false}
                    locked={agencyHasConnect === false}
                    lockedCta={{ to: '/dashbo-mcp', label: t('invite_user.feature_connect_upgrade_cta') }}
                />
            </div>

            {panel && (
                <div className="pm-modules-panel" style={{ '--pm-active-col': expandedKey === 'dashboard' ? 0 : 1 }}>
                    <div className="pm-modules-panel-header">
                        <span className="pm-modules-panel-title">
                            {expandedKey === 'dashboard'
                                ? t('portal_modules.module_dashboard_name')
                                : reportsModuleName}
                        </span>
                    </div>
                    <div className="pm-modules-panel-body">
                        {panel}
                    </div>
                </div>
            )}

            {/* Memoria del cliente (solo lo compartido, read-only). Requiere el módulo MCP.
                SuperAdmin-only mientras el feature está en dogfood: franja discreta de "interno",
                fuera de la grilla del cliente para no romper la fila ni la continuidad del panel. */}
            {isSuperAdmin && (
                <div className={`pm-memory-strip ${modules.memory ? '-enabled' : ''} ${agencyHasConnect === false ? '-locked' : ''}`}>
                    <span className="pm-memory-strip-icon"><FaIcon iconName="brain" /></span>
                    <div className="pm-memory-strip-texts">
                        <span className="pm-memory-strip-name">
                            {t('portal_modules.module_memory_name')}
                            <span className="pm-memory-strip-tag">{t('portal_modules.internal_tag')}</span>
                        </span>
                        <span className="pm-memory-strip-desc">{t('portal_modules.module_memory_desc')}</span>
                    </div>
                    {agencyHasConnect === false ? (
                        <Link to="/dashbo-mcp" target="_blank" className="pm-memory-strip-lock">
                            <FaIcon iconName="lock" />
                            <span>{t('invite_user.feature_connect_upgrade_cta')}</span>
                        </Link>
                    ) : (
                        <span className="pm-memory-strip-switch">
                            <Switch
                                name="module_memory"
                                checked={!!modules.memory}
                                onChange={(e) => onToggleModule('memory', e.target.checked)}
                                style={{ margin: 0 }}
                            />
                            <span className="pm-memory-strip-switch-label">
                                {modules.memory ? t('portal_modules.toggle_on') : t('portal_modules.toggle_off')}
                            </span>
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}
